import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Loan : famille "prêt d'un bien".
 *
 * Remise temporaire d'un bien de l'organisation à une personne identifiée,
 * gratuite ou facturée, avec échéance de retour et relances. Construite sur le
 * modèle "famille + type" de la bible (patron : `asset`).
 *
 * Le socle ne connaît pas la nature métier concrète ("prêt de livre", "prêt de
 * matériel", "prêt de véhicule") : il connaît la famille générique `loan`. Le
 * vocabulaire métier est porté par le champ `type` (chaîne libre déclarée par
 * l'app), et le cycle de vie (remise, échéance, retour) par les dates `lentAt`
 * / `dueBackAt` / `returnedAt`.
 *
 * Un prêt relie toujours deux extrémités, cœur de la famille donc requises : le
 * bien prêté (`itemAssetId`, une fiche `asset`) et l'emprunteur identifié
 * (`borrowerContactId`, une fiche `contact`). Les autres liens (facturation via
 * `order`, `custody`, `inspection`, `incident`) sont de simples ids optionnels ;
 * aucun import d'un autre schéma de famille.
 *
 * Sécurité : org-wide en v0 (même doctrine qu'asset), rien de sensible signalé
 * par la fiche.
 *
 * Convention de patron (cf. asset) :
 *   - `xCoreFields`  : les champs MÉTIER seuls (sans les champs standard).
 *   - `XSchema`      : famille complète = champs standard + champs métier.
 *   - `XCreateInputSchema` : ce que l'appelant fournit à la création, VALIDÉ
 *     AVANT toute écriture Firestore.
 */

/** Champs métier de la famille loan (hors champs standard du socle). */
export const loanCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "livre", "materiel", "vehicule"). */
  type: z.string().min(1),
  /** Bien prêté (référence `asset`, cœur de la famille). */
  itemAssetId: z.string().min(1),
  /** Emprunteur identifié (référence `contact`, cœur de la famille). */
  borrowerContactId: z.string().min(1),
  /** Date de remise du bien (début du prêt). Optionnel. */
  lentAt: z.date().optional(),
  /** Échéance de retour prévue (support des relances). Optionnel. */
  dueBackAt: z.date().optional(),
  /** Date de retour effective (fin du prêt). Optionnel. */
  returnedAt: z.date().optional(),
  /** Notes libres sur l'état du bien (remise et retour). Optionnel. */
  conditionNotes: z.string().min(1).optional(),
  /** Le prêt est-il facturé (vrai) ou gratuit (faux) ? Optionnel. */
  billable: z.boolean().optional(),
  /** Commande `order` rattachée si le prêt est facturé. Optionnel. */
  orderId: z.string().min(1).optional(),
  /** Prise en charge `custody` dont découle le prêt. Optionnel. */
  custodyId: z.string().min(1).optional(),
  /** Inspections d'état rattachées (ids de fiches `inspection`, remise et retour). Optionnel. */
  conditionInspectionIds: z.array(z.string()).optional(),
  /** Incident survenu pendant le prêt (référence `incident`). Optionnel. */
  incidentId: z.string().min(1).optional(),
} as const;

/** Schéma complet de la famille loan (champs standard + champs métier). */
export const LoanSchema = z.object({
  ...baseEntityFields,
  ...loanCoreFields,
});

/** Type d'un loan. */
export type Loan = z.infer<typeof LoanSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const LoanCreateInputSchema = z.object({
  ...loanCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'un loan. */
export type LoanCreateInput = z.infer<typeof LoanCreateInputSchema>;

/**
 * Schéma du patch de mise à jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont gérés par le SDK : aucun des deux n'est accepté ici.
 */
export const LoanUpdateInputSchema = z
  .object({ ...loanCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise à jour d'un loan. */
export type LoanUpdateInput = z.infer<typeof LoanUpdateInputSchema>;
